import { capitalize, isEqual } from 'lodash-es';
import { formatNumber } from 'utils/number';
import {
  AreaConfigResponse,
  FormationMapping,
  TypeWellConfigRow,
  WaterForecastOption,
  WaterForecastSource,
} from './types';
import { DEFAULT_WATER_RATIO, INITIAL_AREA_CONFIG } from './default-values';

export const getChangedObject = <T extends Record<string, any>>(
  current: T | null,
  baseline: T | null,
  includeId = false
): Partial<T> => {
  if (!current) return {};
  if (!baseline) return current;

  const changed: Partial<T> = {};
  for (const key of Object.keys(current) as Array<keyof T>) {
    if (key === 'id' && includeId) {
      changed[key] = current[key];
      continue;
    }
    if (!isEqual(current[key], baseline[key])) {
      changed[key] = current[key];
    }
  }
  return changed;
};

/**
 * Builds the display name for an area config.
 * Format: basinName - subAreas - operators - developmentType
 * Each segment is only included if it has data.
 * @param {AreaConfigResponse} config - The area config response
 * @param {string} fallback - Fallback when basin name is missing
 * @return {string} The display name
 */
export const buildConfigDisplayName = (
  config: AreaConfigResponse,
  fallback = 'New Area'
): string => {
  const basinName = config.basinName;
  if (!basinName) return fallback;

  const parts: string[] = [basinName];

  const subAreaNames = (config.subAreas || [])
    .map((sa) => sa.typeCurveArea?.name || sa.developmentArea?.name)
    .filter(Boolean);
  if (subAreaNames.length > 0) {
    parts.push(subAreaNames.join(', '));
  }

  if (config.operatorNames && config.operatorNames.length > 0) {
    parts.push(config.operatorNames.join(', '));
  }

  if (config.developmentType) {
    parts.push(capitalize(config.developmentType));
  }

  return parts.join(' - ');
};

/** The ids of the selected area config, kept between renders. */
export interface SelectedConfigIds {
  id: string | null;
  elementId: string | null;
}

/**
 * Checks whether the selected area config changed because the new, unsaved
 * config was saved, and not because the user picked another config. Saving
 * replaces the new config's 'local' id with its saved id but keeps its
 * elementId.
 * @param {SelectedConfigIds} previous - The config selected before
 * @param {SelectedConfigIds} current - The config selected now
 * @return {boolean} True when the new config was saved
 */
export const isNewConfigSaved = (
  previous: SelectedConfigIds,
  current: SelectedConfigIds
): boolean =>
  previous.id === INITIAL_AREA_CONFIG.id &&
  !!current.id &&
  current.id !== INITIAL_AREA_CONFIG.id &&
  current.elementId === previous.elementId;

/**
 * Checks whether a Type Curve Formation name is filled in.
 * @param {string | null | undefined} name - The name to check
 * @return {boolean} True when the name is not blank
 */
const isFormationName = (name: string | null | undefined): name is string =>
  !!name?.trim();

/**
 * Lists the Type Curve Formation names in the Formations table, in table
 * order. Each name appears once. Names that differ only by case are kept as
 * different names.
 * @param {FormationMapping[]} mappings - The Formations table rows
 * @return {string[]} The formation names
 */
const formationNames = (mappings: FormationMapping[]): string[] => {
  const names: string[] = [];
  for (const mapping of mappings) {
    const name = mapping.typeCurveFormation;
    if (isFormationName(name) && !names.includes(name)) names.push(name);
  }
  return names;
};

/**
 * Returns a key for a Formations table row that stays the same when the user
 * edits the row, including before the row is first saved.
 * @param {FormationMapping} mapping - A Formations table row
 * @return {string} The row's key
 */
const formationMappingKey = (mapping: FormationMapping): string =>
  `${mapping.geologicInterval}|${mapping.positionalIndex}`;

/**
 * Puts saved Type Well Profile Config rows in the order they appear in
 * another list, matched by id. Rows that are not in that list go last, in
 * their current order.
 * @param {TypeWellConfigRow[]} rows - The rows to put in order
 * @param {TypeWellConfigRow[]} ordered - The rows in the order to follow
 * @return {TypeWellConfigRow[]} The same rows, in the new order
 */
export const orderRowsLike = (
  rows: TypeWellConfigRow[],
  ordered: TypeWellConfigRow[]
): TypeWellConfigRow[] => {
  const positions = new Map(ordered.map((row, index) => [row.id, index]));
  // A row's place in `ordered`, or the end when it is not there.
  const position = (row: TypeWellConfigRow) => {
    const found = row.id === null ? undefined : positions.get(row.id);
    return found ?? ordered.length;
  };
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => position(a.row) - position(b.row) || a.index - b.index)
    .map(({ row }) => row);
};

/**
 * Builds the Type Well Profile Config rows from the Formations table.
 *
 * Each Type Curve Formation gets one row, in Formations table order, and the
 * Fallback row is always last. A row whose formation is still in the table
 * keeps its values. A formation renamed in the table keeps its row under the
 * new name. A formation that is new to the table gets a row with the
 * `newRow` values.
 * @param {object} args - The arguments
 * @param {TypeWellConfigRow[]} args.rows - The current rows
 * @param {FormationMapping[]} args.previousMappings - The Formations table
 *   before the latest change, used to find renamed formations
 * @param {FormationMapping[]} args.mappings - The Formations table now
 * @param {TypeWellConfigRow} args.newRow - The values for added rows
 * @return {TypeWellConfigRow[]} The rows to show
 */
export const buildTypeWellConfigRows = ({
  rows,
  previousMappings,
  mappings,
  newRow,
}: {
  rows: TypeWellConfigRow[];
  previousMappings: FormationMapping[];
  mappings: FormationMapping[];
  newRow: TypeWellConfigRow;
}): TypeWellConfigRow[] => {
  const names = formationNames(mappings);
  const previousNames = formationNames(previousMappings);
  const rowsByName = new Map<string, TypeWellConfigRow>();
  for (const row of rows) {
    if (!row.isFallback && isFormationName(row.tcFormationName)) {
      rowsByName.set(row.tcFormationName, row);
    }
  }

  // A rename is a Formations table row whose name changed from one that is no
  // longer in the table to one that was not in the table before.
  const previousByKey = new Map(
    previousMappings.map((mapping) => [formationMappingKey(mapping), mapping])
  );
  for (const mapping of mappings) {
    const newName = mapping.typeCurveFormation;
    const oldName = previousByKey.get(
      formationMappingKey(mapping)
    )?.typeCurveFormation;
    if (!isFormationName(newName) || !isFormationName(oldName)) continue;
    if (names.includes(oldName) || previousNames.includes(newName)) continue;
    const row = rowsByName.get(oldName);
    if (!row || rowsByName.has(newName)) continue;
    rowsByName.delete(oldName);
    rowsByName.set(newName, { ...row, tcFormationName: newName });
  }

  const fallbackRow = rows.find((row) => row.isFallback) ?? {
    ...newRow,
    isFallback: true,
    tcFormationName: null,
  };

  return [
    ...names.map(
      (name) =>
        rowsByName.get(name) ?? {
          ...newRow,
          isFallback: false,
          tcFormationName: name,
        }
    ),
    fallbackRow,
  ];
};

/**
 * Sets a row's Water Forecast Source. The ratio input for the new source
 * starts at its default, and the other source's input is cleared.
 * @param {TypeWellConfigRow} row - The row to change
 * @param {WaterForecastSource} source - The new source
 * @return {TypeWellConfigRow} The changed row
 */
export const withWaterForecastSource = (
  row: TypeWellConfigRow,
  source: WaterForecastSource
): TypeWellConfigRow => {
  if (row.waterForecastSource === source) return row;
  return {
    ...row,
    waterForecastSource: source,
    watercutPct: source === 'oil' ? DEFAULT_WATER_RATIO.oil : null,
    wgr: source === 'gas' ? DEFAULT_WATER_RATIO.gas : null,
  };
};

/**
 * Sets a row's Water Forecast Option. Choosing Ratio picks the source that
 * matches the row's Main Stream, or Oil when the Main Stream is Water.
 * Choosing DCA clears the source and both ratio inputs.
 * @param {TypeWellConfigRow} row - The row to change
 * @param {WaterForecastOption} option - The new option
 * @return {TypeWellConfigRow} The changed row
 */
export const withWaterForecastOption = (
  row: TypeWellConfigRow,
  option: WaterForecastOption
): TypeWellConfigRow => {
  if (row.waterForecastOption === option) return row;
  if (option === 'dca') {
    return {
      ...row,
      waterForecastOption: 'dca',
      waterForecastSource: null,
      watercutPct: null,
      wgr: null,
    };
  }
  return withWaterForecastSource(
    { ...row, waterForecastOption: 'ratio', waterForecastSource: null },
    row.mainStream === 'gas' ? 'gas' : 'oil'
  );
};

/** The error message for each invalid field of a Type Well Profile Config row. */
export type TypeWellConfigErrors = Partial<
  Record<
    'gasShrinkPct' | 'nglYield' | 'waterForecastSource' | 'watercutPct' | 'wgr',
    string
  >
>;

/**
 * Returns the error for a number field that must be filled in and within a
 * range.
 * @param {number | null} value - The value to check
 * @param {string} label - The field's name in the message
 * @param {number} min - The lowest allowed value
 * @param {number} max - The highest allowed value, unless `excludeMax` is set
 * @param {boolean} excludeMax - True when the value must be less than `max`
 * @return {string | undefined} The error, or undefined when the value is valid
 */
const rangeError = (
  value: number | null,
  label: string,
  min: number,
  max: number,
  excludeMax = false
): string | undefined => {
  const format = (n: number) => formatNumber(n, 0, 2, 'decimal', true);
  if (value === null) return `${label} is required`;
  if (excludeMax && value >= max) {
    return `${label} must be less than ${format(max)}`;
  }
  if (value < min || value > max) {
    return `${label} must be between ${format(min)} and ${format(max)}`;
  }
  return undefined;
};

/**
 * Checks a Type Well Profile Config row against the allowed ranges. A row
 * with any error cannot be saved.
 * @param {TypeWellConfigRow} row - The row to check
 * @return {TypeWellConfigErrors} The errors, empty when the row is valid
 */
export const getTypeWellConfigErrors = (
  row: TypeWellConfigRow
): TypeWellConfigErrors => {
  const checks: TypeWellConfigErrors = {
    gasShrinkPct: rangeError(row.gasShrinkPct, 'Gas Shrink Factor', 0, 100),
    nglYield: rangeError(row.nglYield, 'NGL Yield', 0, 10_000),
  };
  if (row.waterForecastOption === 'ratio') {
    if (row.waterForecastSource === null) {
      checks.waterForecastSource = 'Water Forecast Source is required';
    }
    if (row.waterForecastSource === 'oil') {
      checks.watercutPct = rangeError(
        row.watercutPct,
        'Watercut',
        0,
        100,
        true
      );
    }
    if (row.waterForecastSource === 'gas') {
      checks.wgr = rangeError(row.wgr, 'WGR', 0, 10_000);
    }
  }
  return Object.fromEntries(
    Object.entries(checks).filter(([, error]) => error !== undefined)
  );
};

/**
 * Checks whether any Type Well Profile Config row has an error.
 * @param {TypeWellConfigRow[]} rows - The rows to check
 * @return {boolean} True when saving should be blocked
 */
export const hasTypeWellConfigErrors = (rows: TypeWellConfigRow[]): boolean =>
  rows.some((row) => Object.keys(getTypeWellConfigErrors(row)).length > 0);
